type PermissionModuleSubgroup = {
  labelKey: string;
  keys: readonly string[];
};

export type PermissionModuleSectionWithGroups = {
  id?: string;
  labelKey: string;
  descriptionKey?: string;
  keys: readonly string[];
  groups?: readonly PermissionModuleSubgroup[];
};

type SubgroupDef = {
  labelKey: string;
  prefixes: readonly string[];
};

function keyMatchesPrefix(key: string, prefix: string): boolean {
  return key === prefix || key.startsWith(`${prefix}.`);
}

function partitionKeys(
  keys: readonly string[],
  defs: readonly SubgroupDef[],
): PermissionModuleSubgroup[] {
  const remaining = new Set(keys);
  const groups: PermissionModuleSubgroup[] = [];

  for (const def of defs) {
    const matched = keys.filter(
      (key) => remaining.has(key) && def.prefixes.some((prefix) => keyMatchesPrefix(key, prefix)),
    );
    if (matched.length === 0) continue;
    for (const key of matched) remaining.delete(key);
    groups.push({ labelKey: def.labelKey, keys: matched });
  }

  if (remaining.size > 0) {
    groups.push({ labelKey: "permissions.drawer.subgroups.other", keys: [...remaining] });
  }

  return groups;
}

const SETTINGS_SECTION_SUBGROUPS: readonly SubgroupDef[] = [
  {
    labelKey: "settings.sections.empresa",
    prefixes: ["settings.organization", "settings.users"],
  },
  {
    labelKey: "settings.sections.cadastros",
    prefixes: [
      "settings.partners",
      "settings.financeLookups",
      "settings.fiscal",
      "settings.materials",
      "settings.equipments",
      "settings.workers",
      "settings.lookups",
    ],
  },
  {
    labelKey: "settings.sections.ferramentas",
    prefixes: [
      "project.projectReport.manageModels",
      "settings.serviceFrontModels",
      "settings.checklistModels",
      "settings.occurrences",
      "settings.documentCategories",
      "settings.photoTags",
    ],
  },
];

const INSIDE_PROJECT_SUBGROUPS: readonly SubgroupDef[] = [
  {
    labelKey: "permissions.drawer.subgroups.overview",
    prefixes: ["project.overview", "project.tracking"],
  },
  {
    labelKey: "permissions.drawer.subgroups.serviceFronts",
    prefixes: ["project.projectServiceFront"],
  },
  { labelKey: "permissions.drawer.subgroups.diary", prefixes: ["project.projectDiary"] },
  { labelKey: "permissions.drawer.subgroups.occurrences", prefixes: ["project.occurrences"] },
  { labelKey: "permissions.drawer.subgroups.materials", prefixes: ["project.materials"] },
  { labelKey: "permissions.drawer.subgroups.equipments", prefixes: ["project.equipments"] },
  { labelKey: "permissions.drawer.subgroups.clientBudgets", prefixes: ["project.clientBudgets"] },
  { labelKey: "permissions.drawer.subgroups.budgets", prefixes: ["project.budgets"] },
  {
    labelKey: "permissions.drawer.subgroups.purchaseRequests",
    prefixes: ["project.purchaseRequests"],
  },
  { labelKey: "permissions.drawer.subgroups.quotations", prefixes: ["project.quotations"] },
  {
    labelKey: "permissions.drawer.subgroups.purchaseOrders",
    prefixes: ["project.purchaseOrders"],
  },
  { labelKey: "permissions.drawer.subgroups.costs", prefixes: ["project.finance"] },
  { labelKey: "permissions.drawer.subgroups.documents", prefixes: ["project.documents"] },
  { labelKey: "permissions.drawer.subgroups.photos", prefixes: ["project.photos"] },
  { labelKey: "permissions.drawer.subgroups.reports", prefixes: ["project.projectReport"] },
];

const FINANCE_SECTION_SUBGROUPS: readonly SubgroupDef[] = [
  { labelKey: "permissions.drawer.subgroups.payables", prefixes: ["finance.payables"] },
  { labelKey: "permissions.drawer.subgroups.receivables", prefixes: ["finance.receivables"] },
  { labelKey: "permissions.drawer.subgroups.cashFlow", prefixes: ["finance.cashFlow"] },
  { labelKey: "permissions.drawer.subgroups.fiscal", prefixes: ["finance.fiscal"] },
];

const SUBGROUPS_BY_SECTION_ID: Partial<Record<string, readonly SubgroupDef[]>> = {
  settingsSections: SETTINGS_SECTION_SUBGROUPS,
  insideProject: INSIDE_PROJECT_SUBGROUPS,
  default: FINANCE_SECTION_SUBGROUPS,
};

export function applyPickerSubgroups<
  T extends {
    id?: string;
    labelKey: string;
    descriptionKey?: string;
    keys: readonly string[];
    groups?: readonly PermissionModuleSubgroup[];
  },
>(section: T): T {
  const defs = section.id ? SUBGROUPS_BY_SECTION_ID[section.id] : undefined;
  if (!defs || section.keys.length < 4) return section;

  const groups = partitionKeys(section.keys, defs);
  if (groups.length <= 1) return section;

  return { ...section, groups };
}
